import { decodeJwtPayload } from '@sportpark/shared/jwt';
import { http, type StoredSession } from './http-client';

import type {
  ConfirmMyPhoneBody as GenConfirmMyPhoneBody,
  MyContactsResponse as GenMyContactsResponse,
  UpdateMyNameBody as GenUpdateMyNameBody,
  LoginChallengeResponse as GenLoginChallengeResponse,
  MyTenantDto as GenMyTenantDto,
  SendOtpResponse as GenSendOtpResponse,
  UserDto as GenUserDto,
} from './generated';

export type MyTenant = GenMyTenantDto;

type PasswordSignIn =
  | { kind: 'session'; session: StoredSession }
  | { kind: 'challenge'; challenge: GenLoginChallengeResponse };


export interface AuthTokenPair {
  accessToken: string;
  refreshToken?: string;
  expiresAt: string | number;
}

export interface AuthUserDto extends Pick<GenUserDto, 'hasPassword' | 'clubTimezone'> {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  roles?: string[];
  permissions?: string[];
  tenantId?: string | null;
  tenantName?: string | null;
}

export interface AuthResponseDto extends AuthTokenPair {
  user: AuthUserDto;
}

const claimList = (payload: Record<string, unknown> | null, ...keys: string[]): string[] => {
  if (!payload) return [];
  for (const key of keys) {
    const raw = payload[key];
    if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === 'string');
    if (typeof raw === 'string') return [raw];
  }
  return [];
};

const permissionsFromJwt = (token: string): string[] =>
  claimList(decodeJwtPayload(token), 'permission', 'permissions');

const rolesFromJwt = (token: string): string[] =>
  claimList(decodeJwtPayload(token), 'role', 'roles');

export const isImpersonationToken = (token: string): boolean =>
  decodeJwtPayload(token)?.impersonation === true;

const sessionFromToken = (
  accessToken: string,
  expiresAt: string | number,
  tenantName: string | null,
): StoredSession => {
  const payload = decodeJwtPayload(accessToken) ?? {};
  const ms = typeof expiresAt === 'number' ? expiresAt : new Date(expiresAt).getTime();
  return {
    accessToken,
    expiresAt: ms,
    user: {
      id: typeof payload.sub === 'string' ? payload.sub : '',
      email: typeof payload.email === 'string' ? payload.email : '',
      phone: null,
      firstName: null,
      lastName: null,
      roles: rolesFromJwt(accessToken),
      permissions: permissionsFromJwt(accessToken),
      tenantId: typeof payload.tenant_id === 'string' ? payload.tenant_id : null,
      tenantName,
    },
  };
};

interface ImpersonationResponseDto {
  accessToken: string;
  expiresAt: string | number;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
}

interface ImpersonationExitDto {
  accessToken: string;
  refreshToken: string;
  expiresAt: string | number;
}

export interface TenantOption {
  id: string;
  slug: string;
  name: string;
  role: 'SuperAdmin' | 'ClubOwner' | 'Manager' | 'Receptionist' | 'Trainer';
}

const toSession = (dto: AuthResponseDto): StoredSession => {
  const expiresAt =
    typeof dto.expiresAt === 'number'
      ? dto.expiresAt
      : new Date(dto.expiresAt).getTime();
  const permissionsFromToken = permissionsFromJwt(dto.accessToken);
  return {
    accessToken: dto.accessToken,
    expiresAt,
    user: {
      id: dto.user.id,
      email: dto.user.email,
      phone: dto.user.phoneNumber ?? null,
      firstName: dto.user.firstName ?? null,
      lastName: dto.user.lastName ?? null,
      roles: dto.user.roles ?? [],
      permissions: permissionsFromToken.length > 0
        ? permissionsFromToken
        : dto.user.permissions ?? [],
      tenantId: dto.user.tenantId ?? null,
      tenantName: dto.user.tenantName ?? null,
      hasPassword: dto.user.hasPassword,
      clubTimezone: dto.user.clubTimezone ?? null,
    },
  };
};

export const authApi = {
  async signInWithPassword(payload: {
    login: string;
    password: string;
    tenantSlug?: string;
  }): Promise<PasswordSignIn> {
    const dto = await http.post<AuthResponseDto | GenLoginChallengeResponse>(
      '/auth/login',
      { email: payload.login, password: payload.password, tenantSlug: payload.tenantSlug },
      { skipAuth: true },
    );
    return 'challengeId' in dto
      ? { kind: 'challenge', challenge: dto }
      : { kind: 'session', session: toSession(dto) };
  },

  async confirmSignIn(payload: { challengeId: string; code: string }): Promise<StoredSession> {
    const dto = await http.post<AuthResponseDto>('/auth/login/confirm', payload, { skipAuth: true });
    return toSession(dto);
  },

  async requestPasswordCode(login: string): Promise<void> {
    await http.post<GenSendOtpResponse>('/auth/password/set/send-code', { login }, { skipAuth: true });
  },

  async setPasswordByCode(payload: { login: string; smsCode: string; newPassword: string }): Promise<void> {
    await http.post<void>('/auth/password/set', payload, { skipAuth: true });
  },

  async listMyTenants(call?: { signal?: AbortSignal }): Promise<MyTenant[]> {
    return http.get<MyTenant[]>('/auth/tenants/mine', { signal: call?.signal });
  },

  async switchTenant(payload: { tenantId: string }): Promise<StoredSession> {
    const dto = await http.post<AuthResponseDto>('/auth/tenants/switch', payload);
    return toSession(dto);
  },

  async impersonate(
    tenantId: string,
    reason: string,
  ): Promise<{ session: StoredSession; tenant: { id: string; name: string; slug: string } }> {
    const dto = await http.post<ImpersonationResponseDto>(
      `/platform/tenants/${tenantId}/impersonate`,
      { reason },
    );
    return {
      session: sessionFromToken(dto.accessToken, dto.expiresAt, dto.tenantName),
      tenant: { id: dto.tenantId, name: dto.tenantName, slug: dto.tenantSlug },
    };
  },

  async exitImpersonation(): Promise<StoredSession> {
    const dto = await http.post<ImpersonationExitDto>('/platform/impersonation/exit', {});
    return sessionFromToken(dto.accessToken, dto.expiresAt, null);
  },

  async logout(): Promise<void> {
    try {
      await http.post<void>('/auth/logout');
    } catch {
    }
  },

  async logoutEverywhere(): Promise<void> {
    await http.post<void>('/auth/logout-all');
  },

  async changePassword(payload: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    await http.post<void>('/auth/change-password', payload);
  },

  async updateMyName(body: GenUpdateMyNameBody): Promise<GenMyContactsResponse> {
    return http.patch<GenMyContactsResponse>('/auth/me', body);
  },

  async sendMyPhoneCode(phone: string): Promise<GenSendOtpResponse> {
    return http.post<GenSendOtpResponse>('/auth/me/phone/send-code', { phone });
  },

  async confirmMyPhone(body: GenConfirmMyPhoneBody): Promise<GenMyContactsResponse> {
    return http.post<GenMyContactsResponse>('/auth/me/phone', body);
  },

  async setPasswordByInvite(payload: {
    userId: string;
    token: string;
    newPassword: string;
  }): Promise<void> {
    await http.post<void>('/auth/invite/set-password', payload, { skipAuth: true });
  },
};
